import equal from "fast-deep-equal";
import type { PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type {
  GroupBasedAuthorization,
  GroupForSharing,
  ReportAuthorization,
  ReportAuthorizationInfo,
  UpdateReportAuthorizationRequest,
  UserBasedAuthorization,
  UserForSharing,
} from "../../../../../../../../../api/types/svTypes.ts";
import RemoveOwnAccessDialog from "../RemoveOwnAccessDialog.tsx";
import type { CurrentUser, ReportCompany, SharedReport } from "../ShareReport.types.ts";
import { SharingReportContext } from "./SharingReportContext.tsx";

interface Props {
  report: SharedReport;
  company: ReportCompany;
  currentUser: CurrentUser;
  owner: UserBasedAuthorization | undefined;
  defaultAuthorization: ReportAuthorization;
  defaultInheritFromParent: boolean;
  onAuthorizationUpdated: (authorization: ReportAuthorizationInfo, inheritFromParent: boolean) => void;
  onClose: () => void;
  updateReportSharing: (
    reportId: string,
    request: UpdateReportAuthorizationRequest
  ) => Promise<ApiResponse<ReportAuthorizationInfo>>;
}

export const EMPTY_GROUP_ID = "00000000-0000-0000-0000-000000000000";

export const SharingReportContextProvider = ({
  report,
  company,
  currentUser,
  owner,
  defaultAuthorization,
  defaultInheritFromParent,
  onClose,
  onAuthorizationUpdated,
  children,
  updateReportSharing,
}: PropsWithChildren<Props>) => {
  const canInherit = Boolean(report.groupId) && report.groupId !== EMPTY_GROUP_ID;
  const [inheritFromParent, setInheritFromParent] = useState<boolean>(canInherit && defaultInheritFromParent);

  const [showRemoveAccessDialog, setShowRemoveAccessDialog] = useState({
    maximumAccess: ReportAccessType.NoAccess,
    show: false,
  });
  const [sharedForCompanyAccess, setSharedForCompanyAccess] = useState<ReportAccessType>(
    defaultAuthorization.companyShared.access
  );

  const [authorizedUsers, setAuthorizedUsers] = useState<UserBasedAuthorization[]>(() => {
    return structuredClone(defaultAuthorization.users);
  });

  const [authorizedGroups, setAuthorizedGroups] = useState<GroupBasedAuthorization[]>(() => {
    return structuredClone(defaultAuthorization.groups);
  });

  const reportAuthorization = useMemo(() => {
    return buildAuthorizationModel(authorizedUsers, authorizedGroups, sharedForCompanyAccess);
  }, [authorizedUsers, authorizedGroups, sharedForCompanyAccess]);

  const isAuthorizationChanged = useMemo(() => {
    return !equal(reportAuthorization, defaultAuthorization) || inheritFromParent !== defaultInheritFromParent;
  }, [defaultAuthorization, reportAuthorization, inheritFromParent, defaultInheritFromParent]);

  const handleOnUsersSelected = useCallback(
    (users: UserForSharing[], access: ReportAccessType) => {
      const newUsers = users.map((u): UserBasedAuthorization => ({ userId: u.userId, access }));
      setAuthorizedUsers([...authorizedUsers, ...newUsers]);
    },
    [authorizedUsers, setAuthorizedUsers]
  );

  const handleOnGroupsSelected = useCallback(
    (groups: GroupForSharing[], access: ReportAccessType) => {
      const newGroups = groups.map((g): GroupBasedAuthorization => ({ groupId: g.groupId, access }));
      setAuthorizedGroups([...authorizedGroups, ...newGroups]);
    },
    [authorizedGroups]
  );

  const handleSharedForCompanyAccessChanged = useCallback((access: ReportAccessType) => {
    setSharedForCompanyAccess(access);
  }, []);

  const handleUserAccessChanged = useCallback((userId: string, access: ReportAccessType) => {
    setAuthorizedUsers((prev) => {
      if (access === ReportAccessType.Owner) {
        const currentOwner = prev.find((u) => u.access === ReportAccessType.Owner);
        if (currentOwner) {
          return prev.map((u) => {
            if (u.userId === currentOwner.userId) {
              return {
                ...u,
                access: ReportAccessType.Read,
              };
            }
            if (u.userId === userId) {
              return { ...u, access };
            }
            return u;
          });
        }
      }
      return prev.map((u) => {
        if (u.userId === userId) {
          return { ...u, access };
        }
        return u;
      });
    });
  }, []);

  const handleGroupAccessChanged = useCallback((groupId: string, access: ReportAccessType) => {
    setAuthorizedGroups((prev) => prev.map((g) => (g.groupId === groupId ? { ...g, access } : g)));
  }, []);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setAuthorizedUsers(authorizedUsers.filter((u) => u.userId !== userId));
    },
    [authorizedUsers, setAuthorizedUsers]
  );

  const handleRemoveGroup = useCallback(
    (groupId: string) => {
      setAuthorizedGroups(authorizedGroups.filter((g) => g.groupId !== groupId));
    },
    [authorizedGroups]
  );

  const sendSharingUpdate = useCallback(
    () =>
      updateReportSharing(report.reportId, {
        clientCode: company.clientCode,
        authorization: reportAuthorization,
        inheritFromParent,
      }),
    [company.clientCode, inheritFromParent, report.reportId, reportAuthorization, updateReportSharing]
  );
  const { request: saveSharing, error: saveError, isLoading: isSaving } = useRequest(sendSharingUpdate);

  const updateAuth = useCallback(async (): Promise<boolean> => {
    const [updatedAuthorization, error] = await saveSharing();
    if (error) {
      if (!error.isCanceledRequest) {
        logError(
          { ...error, message: "Failed to update report sharing" },
          "[SharingReportContextProvider] updateReportSharing"
        );
      }
      return false;
    }
    onAuthorizationUpdated(updatedAuthorization, inheritFromParent);
    return true;
  }, [inheritFromParent, onAuthorizationUpdated, saveSharing]);

  const getMaximumCurrentUserAccess = useCallback(() => {
    if (report.authorization.access === ReportAccessType.Supervisor) {
      return ReportAccessType.Supervisor;
    }

    const currentUserAccess = authorizedUsers.find((u) => u.userId === currentUser.id);
    if (currentUserAccess) {
      return currentUserAccess.access > sharedForCompanyAccess ? currentUserAccess.access : sharedForCompanyAccess;
    }

    return sharedForCompanyAccess;
  }, [authorizedUsers, currentUser.id, report.authorization.access, sharedForCompanyAccess]);

  const handleSave = useCallback(async () => {
    if (!isAuthorizationChanged) {
      return;
    }

    // When inheriting, the effective access is governed by the parent group and the report's own owner is
    // always preserved server-side, so the remove-own-access confirmation does not apply.
    if (!inheritFromParent) {
      const maximumAccess = getMaximumCurrentUserAccess();
      if (
        report.authorization.access !== ReportAccessType.Supervisor &&
        !checkAccess(maximumAccess, ReportAccessType.FullAccess)
      ) {
        setShowRemoveAccessDialog({
          maximumAccess,
          show: true,
        });
        return;
      }
    }

    if (await updateAuth()) {
      onClose();
    }
  }, [
    getMaximumCurrentUserAccess,
    inheritFromParent,
    isAuthorizationChanged,
    onClose,
    report.authorization.access,
    updateAuth,
  ]);

  const handleChangeAccessConfirmed = useCallback(async () => {
    const isSaved = await updateAuth();
    setShowRemoveAccessDialog({ ...showRemoveAccessDialog, show: false });
    if (isSaved) {
      onClose();
    }
  }, [onClose, showRemoveAccessDialog, updateAuth]);

  const onCancelRemoveAccessConfirmation = useCallback(() => {
    setShowRemoveAccessDialog({
      maximumAccess: ReportAccessType.NoAccess,
      show: false,
    });
  }, []);

  return (
    <>
      <SharingReportContext
        value={{
          report,
          currentUser,
          owner,
          company,
          sharedForCompanyAccess,
          authorizedUsers,
          authorizedGroups,
          isAuthorizationChanged,
          isSaving,
          saveError,
          setSharedForCompanyAccess: handleSharedForCompanyAccessChanged,
          setAuthorizedUsers,
          onUserSelected: handleOnUsersSelected,
          onUserAccessChanged: handleUserAccessChanged,
          onRemoveUser: handleRemoveUser,
          onGroupSelected: handleOnGroupsSelected,
          onGroupAccessChanged: handleGroupAccessChanged,
          onRemoveGroup: handleRemoveGroup,
          canInherit,
          inheritFromParent,
          setInheritFromParent,
          onSave: handleSave,
        }}
      >
        {children}
      </SharingReportContext>
      {showRemoveAccessDialog.show && (
        <RemoveOwnAccessDialog
          report={report}
          access={showRemoveAccessDialog.maximumAccess}
          onCancel={onCancelRemoveAccessConfirmation}
          onRemove={handleChangeAccessConfirmed}
        />
      )}
    </>
  );
};
function buildAuthorizationModel(
  authorizedUsers: UserBasedAuthorization[],
  authorizedGroups: GroupBasedAuthorization[],
  sharedForCompanyAccess: ReportAccessType
) {
  const auth: ReportAuthorization = {
    users: authorizedUsers,
    groups: authorizedGroups,
    companyShared: {
      access: sharedForCompanyAccess,
    },
  };
  return auth;
}
